<script lang="ts">
	import { real_id } from 'src/helpers/interface_helpers';
	import { devices_status } from 'src/stores';
	import { format } from 'svelte-i18n';

	import type { Device } from 'src/types/Device';

	import ModalPrompt from 'src/components/ModalPrompt.svelte';

	import { Alert, Button, toast } from '@Shelly/shelly-design-system';

	import {
		findZWaveGatewayForDevice,
		isZWaveGatewayRecoveryActionEnabled,
	} from 'src/helpers/z_wave_gateway_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { zwaveDeviceReinterview } from 'src/services/xz_http_reqs';

	export let device: Device;

	$: status = $devices_status?.[real_id(device?.id)];
	$: zwaveGateway = findZWaveGatewayForDevice(device, status);
	$: zwaveGatewayStatus = zwaveGateway ? $devices_status?.[real_id(zwaveGateway.id)] : undefined;
	$: isReinterviewEnabled = isZWaveGatewayRecoveryActionEnabled(device, status, zwaveGatewayStatus);

	let showModal = false;
	let loading = false;

	const reinterviewDevice = async () => {
		if (loading || !isReinterviewEnabled) return;
		loading = true;

		const res = await zwaveDeviceReinterview(device);
		if (res?.isok) {
			toast.open({
				type: 'success',
				title: $format('zwave_reinterview_scc_toast', {
					default: 'Reinterview started for the Z-Wave device.',
				}),
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('zwave_reinterview_err_toast', {
					default: 'Failed to reinterview the Z-Wave device.',
				}),
			});
		}

		loading = false;
	};

	$: modalProps = {
		title: $format('prompt_title_zwave_reinterview', { default: 'Reinterview device?' }),
		text: $format('prompt_text_zwave_reinterview', {
			default:
				'The gateway will rediscover this node’s capabilities and configuration parameters. This can take a while and may briefly interrupt controls.',
		}),
		button_2: $format('button_cancel', { default: 'Cancel' }),
		button_1: $format('button_zwave_reinterview', { default: 'Reinterview' }),
		callback: reinterviewDevice,
	};
</script>

{#if isReinterviewEnabled}
	<Alert
		class="mb-s"
		type="info"
		heading={$format('alert_heading_zwave_reinterview', {
			default: 'Reinterview this Z-Wave device through the gateway.',
		})}
	/>
{:else}
	<Alert
		class="mb-s"
		type="info"
		heading={$format('alert_heading_zwave_reinterview_disabled', {
			default:
				'Available only when this device is offline or unresponsive and its Wave gateway is online.',
		})}
	/>
{/if}

<Button
	{loading}
	disabled={!isReinterviewEnabled}
	size="m"
	width="full"
	on:click={() => (showModal = true)}
>
	{capFirstChar($format('button_zwave_reinterview', { default: 'Reinterview' }))}
</Button>

{#if showModal}
	<ModalPrompt bind:showModal props={modalProps} />
{/if}
